import usePermissions from "@/hooks/use-permissions.hook.ts";
import { WorkflowFolderCreateModal } from "@/pages/workflows/edit/components/WorkflowFolderCreateModal.tsx";
import {
  useWorkflowFolderDelete,
  useWorkflowFolders,
} from "@/queries/workflows/workflow-folder.query.ts";
import { useWorkflowsSearch } from "@/queries/workflows/workflow.query.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  ButtonLink,
  Card,
  ConfirmationModal,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  Stack,
  Tag,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export const WorkflowLibraryFoldersView = () => {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const { openModal } = useModals();
  const { data: workflowFolders, isLoading: isWorkflowFoldersLoading } = useWorkflowFolders();
  const workflowFolderDelete = useWorkflowFolderDelete();
  const { data: allWorkflows } = useWorkflowsSearch({
    filter: { page_number: 1 },
  });
  const canManageWorkflowFolders = canIEdit(ApiPermission.workflows_manage_all);

  return (
    <Page>
      <Page.Header title={t("common:library")}>
        {canIEdit(ApiPermission.workflows_create) && (
          <ButtonLink href={"/workflows/new"}>{t("workflow:create_new")}</ButtonLink>
        )}
      </Page.Header>

      <Page.Content>
        <Stack
          direction={"horizontal"}
          gap={"md"}
          className={"grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 px-16"}
        >
          {isWorkflowFoldersLoading ? (
            <>
              <Card isLoading={true} />
              <Card isLoading={true} />
              <Card isLoading={true} />
            </>
          ) : (
            <>
              <Card
                header={t("workflow:all_workflows")}
                to={"/workflows/library/all"}
                buttonsSlot={
                  <Tag>
                    {t("plurals:workflows_count", {
                      count: allWorkflows?.metadata.total_items ?? 0,
                    })}
                  </Tag>
                }
              />

              {(workflowFolders ?? []).map((folder) => (
                <Card
                  key={folder.id}
                  header={folder.title}
                  to={`/workflows/library/${folder.id}`}
                  buttonsSlot={
                    <Tag>{t("plurals:workflows_count", { count: folder.workflow_count })}</Tag>
                  }
                  menu={
                    canManageWorkflowFolders && (
                      <>
                        <DropdownMenuItem
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            openModal(WorkflowFolderCreateModal, { folder });
                          }}
                          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                          title={t("workflow:edit_category")}
                        />
                        <DropdownMenuItem
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            if (folder.workflow_count > 0) return;
                            e.preventDefault();
                            openModal(ConfirmationModal, {
                              question: t("workflow:delete_category_named", {
                                title: folder.title,
                              }),
                              text: t("workflow:delete_category_confirm", {
                                title: folder.title,
                              }),
                              acceptTitle: t("common:delete"),
                              onAccept: async () => {
                                await workflowFolderDelete.mutateAsync({ id: folder.id });
                              },
                            });
                          }}
                          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                          variant={folder.workflow_count > 0 ? "menu" : "menuDanger"}
                          disabled={folder.workflow_count > 0}
                          className={
                            folder.workflow_count > 0 ? "pointer-events-none opacity-70" : undefined
                          }
                          title={
                            folder.workflow_count > 0
                              ? t("workflow:delete_category_unavailable")
                              : t("workflow:delete_category")
                          }
                          description={
                            folder.workflow_count > 0
                              ? t("workflow:delete_category_contains_workflows")
                              : undefined
                          }
                        />
                      </>
                    )
                  }
                />
              ))}

              {canManageWorkflowFolders && (
                <Card
                  icon={"fa-plus"}
                  header={t("workflow:create_category")}
                  caption={t("workflow:create_category_description")}
                  onClick={() => {
                    openModal(WorkflowFolderCreateModal, {});
                  }}
                  variant={"action"}
                />
              )}
            </>
          )}
        </Stack>
      </Page.Content>
    </Page>
  );
};
